import { useQueryClient } from '@tanstack/react-query'
import {
  Collapse,
  Flex,
  Typography,
  FormInstance,
  message,
  DatePicker
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { DefaultOptionType } from 'antd/es/select'
import { FormListFieldData } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePatientTreatmentServiceDeletePatientTreatmentPlanAdverseEffectEntryEndpoint,
  usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryEndpointKey,
  usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpoint,
  usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpointKey,
  usePatientTreatmentServiceUpdatePatientTreatmentPlanAdverseEffectEntryEndpoint
} from 'src/api/queries'
import {
  ApiError,
  GetAdverseEffectEntryAdverseEffectListResponse2
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CollapseExpandArrow,
  EditableFormItem,
  SubmitButtons
} from 'src/components'
import {
  dayMonthYearBackSlash,
  disabledFutureDate,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { ReactionsListChildren } from './ReactionsListChildren'
import { useStyles } from './styles'

const { Text } = Typography

type Props = {
  fields: FormListFieldData[]
  form: FormInstance
}

const getReactionId = (form?: FormInstance, name?: number) =>
  form?.getFieldValue(['events', name, 'id'])

const getReportedBy = (form: FormInstance, name: number) => {
  const firstName = form.getFieldValue([
    'events',
    name,
    'reportedBy',
    'firstName'
  ])
  const lastName = form.getFieldValue([
    'events',
    name,
    'reportedBy',
    'lastName'
  ])
  const legalEntityName = form.getFieldValue([
    'events',
    name,
    'legalEntity',
    'name'
  ])

  return `${firstName} ${lastName} - ${legalEntityName}`
}

export const ReactionsList = ({ fields, form }: Props) => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()

  const { styles } = useStyles()
  const [activeKey, setActiveKey] = useState<string[]>([])
  const [editingIndex, setEditingIndex] = useState<number>()
  const [selectedItems, setSelectedItems] = useState<AnyObject>()

  const queryClient = useQueryClient()

  const { data: adverseReactions } =
    usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpoint(
      { id: String(id) },
      [String(id)],
      {
        enabled: !!id
      }
    )

  useEffect(() => {
    if (adverseReactions) {
      form.setFieldsValue({ events: adverseReactions?.data })
      const initialSelectedItems = adverseReactions?.data?.reduce(
        (acc, event, index) => {
          acc[index] =
            event.adverseEffects?.map((item) => ({
              value: String(item.id),
              label: item.translation?.[currentLang?.value] ?? ''
            })) || []
          return acc
        },
        {} as Record<number, { value: string; label: string }[]>
      )
      setSelectedItems(initialSelectedItems)
    }
  }, [adverseReactions, form, currentLang])

  const palette = useTheme()

  const onSave = async () => {
    await form.validateFields()
    const values = form.getFieldValue(['events', editingIndex])

    const adverseEffectIds = values?.adverseEffects?.map(
      (item: DefaultOptionType) => item?.id ?? item?.value
    )

    edit({
      id: String(id),
      adverseEffectEntryId: getReactionId(form, editingIndex),
      requestBody: {
        adverseEffectEntry: {
          adverseEffectIds,
          comments: values?.comments,
          recordedDate: dayjs(values?.recordedDate).format(yearMonthDaySlash)
        }
      }
    })
  }

  const onCancel = (index: number) => {
    form.resetFields(['events', index, 'adverseEffects'])
    setEditingIndex(undefined)
  }

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [
        usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpointKey,
        String(id)
      ]
    })

    queryClient.invalidateQueries({
      queryKey: [
        usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryEndpointKey,
        String(id),
        getReactionId(form, editingIndex)
      ]
    })

    setActiveKey([])
    setEditingIndex(undefined)
  }

  const { mutate: edit } =
    usePatientTreatmentServiceUpdatePatientTreatmentPlanAdverseEffectEntryEndpoint(
      {
        onSuccess: () => {
          onMutationSuccess()
          message.success(t('common.save_success'))
        },
        onError: (e: ApiError) => formErrorHandler(e.body.errors)
      }
    )

  const { mutate: deleteMutation } =
    usePatientTreatmentServiceDeletePatientTreatmentPlanAdverseEffectEntryEndpoint(
      {
        onSuccess: () => {
          onMutationSuccess()
          message.success(t('common.success_delete'))
        },
        onError: (e: ApiError) => formErrorHandler(e.body.errors)
      }
    )

  const onCollapseChange = (keys: string | string[]) => {
    setActiveKey(Array.isArray(keys) ? keys : [keys])
  }

  const items = fields.map(({ name }) => {
    const isEditing = editingIndex === name
    const isExpanded = activeKey.includes(String(name))

    return {
      key: name,
      label: (
        <Flex
          justify="space-between"
          align="center"
          onClick={(e) => {
            if (isExpanded && isEditing) {
              e.stopPropagation()
              e.preventDefault()
            }
          }}
        >
          <Flex gap={24}>
            <Flex gap={4}>
              {!isEditing && <Text strong>{t('common.date')}:</Text>}
              {isEditing ? (
                <EditableFormItem
                  name={[name, 'recordedDate']}
                  label={t('common.date')}
                  isEditing={isEditing}
                  rules={requiredRules}
                  normalize={(m: Dayjs) => dayjs(m).format(yearMonthDaySlash)}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : undefined
                  })}
                >
                  <DatePicker
                    onClick={(e) => e.stopPropagation()}
                    allowClear={false}
                    format={dayMonthYearBackSlash}
                    className={styles.adverseField}
                    disabledDate={disabledFutureDate}
                  />
                </EditableFormItem>
              ) : (
                <Text>
                  {form.getFieldValue(['events', name, 'recordedDate'])}
                </Text>
              )}
            </Flex>

            {!isExpanded && (
              <>
                <Flex gap={4}>
                  <Text strong>{t('consultations.accuse')}:</Text>
                  {form
                    .getFieldValue(['events', name, 'adverseEffects'])
                    ?.map(
                      (item: GetAdverseEffectEntryAdverseEffectListResponse2) =>
                        item?.translation?.[currentLang?.value]
                    )
                    .join(', ')}
                </Flex>

                <Flex gap={4}>
                  <Text
                    style={{
                      color: palette.colorTextTertiary
                    }}
                  >
                    {getReportedBy(form, name)}
                  </Text>
                </Flex>
              </>
            )}
          </Flex>

          {isExpanded && (
            <SubmitButtons
              preventDefault
              showConfirmDelete
              editingIndex={editingIndex}
              isEditing={isEditing}
              onCancel={() => onCancel(name)}
              onEdit={() => setEditingIndex(name)}
              onDelete={() =>
                deleteMutation({
                  id: String(id),
                  adverseEffectEntryId: getReactionId(form, name)
                })
              }
              onSave={onSave}
            />
          )}
        </Flex>
      ),
      children: (
        <ReactionsListChildren
          form={form}
          name={name}
          isEditing={isEditing}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          getReactionId={getReactionId}
          getReportedBy={getReportedBy(form, name)}
        />
      ),
      className: styles.collapseHeaderMargin
    }
  })

  return (
    <Collapse
      items={items}
      bordered={false}
      expandIconPosition="end"
      className={styles.collapseHeader}
      activeKey={activeKey}
      expandIcon={CollapseExpandArrow}
      onChange={onCollapseChange}
    />
  )
}
